// Core domain types for STRATA — attention archaeology dig.
// Psychological distance practice + rich telemetry + charitable interpretation.

export type SiteId = string // st_xxxxxxxxxxxxxxxx private sovereign key. Never shared casually.

export type DigId = string

export type ShardId = string

export type DistanceDim = 'temporal' | 'spatial' | 'social' | 'hypothetical'

export type DistanceVector = Record<DistanceDim, number> // 0 (proximal/concrete) .. 10 (distal/abstract)

export interface Shard {
  id: ShardId
  title: string
  stratum: 0 | 1 | 2 | 3 // 0=surface (last 7d), 1=recent, 2=deep, 3=fossil
  text: string // the recovered fragment — specific, ambivalent, never moralized
  // Pre-written reframings for each dimension extreme. These make the cognitive move *felt*.
  reframings: Record<DistanceDim, { low: string; high: string }>
  seedTags: string[]
}

export interface Interpretation {
  id: string
  digId: DigId
  shardId: ShardId
  distances: DistanceVector
  gloss: string // charitable / generous reading (constrained)
  counterGloss?: string // the harsh read the person chose to surface
  nuanceTags: string[]
  confidence: number // 1-5
  timeSpentMs: number // reflection depth proxy
  createdAt: string
  editedAt?: string
}

export interface ClusterProposal {
  id: string
  digId: DigId
  clusters: Array<{
    label: string
    members: ShardId[]
    avgAffinity: number
  }>
  boundaryCandidates: ShardId[] // items that sit between clusters — key for charity
  method: string // "bge-small + cosine + threshold / user edits"
  createdAt: string
}

export interface TelemetryEvent {
  id?: string
  digId: DigId
  t: number // client monotonic ms since dig start (or wall)
  type: string
  payload: Record<string, unknown>
  clientLatency?: number
}

export interface Dig {
  id: DigId
  siteId: SiteId
  startedAt: string
  endedAt?: string
  shardIds: ShardId[] // the trench composition
  baselineDistances?: DistanceVector // from site calibration
  summary?: {
    interpretiveCharityScore: number
    meanDistanceShift: Partial<DistanceVector>
    nuanceCount: number
    boundaryMarks: number
    clusterEdits: number
    skips: number
    totalEvents: number
  }
}

// The explicit contract: semantic clustering is a tool for seeing structure.
// It never decides meaning or erases the single anomalous case.
export const CLUSTERING_PRINCIPLE = 
  "Embeddings surface topical and tonal overlap. We always expose low-affinity members and require human review of every boundary. Charity is earned in the overrides, not the proposal."

export type NuanceTag =
  | 'Enabling condition'
  | 'Structural constraint'
  | 'Info-seeking'
  | 'Moral licensing'
  | 'Status / image'
  | 'Temporal discounting'
  | 'Social proof'
  | 'Ambiguous intent'
  | 'Anxiety buffer'
  | 'Boundary object'
  | 'Tradeoff acknowledged'
  | 'Habit residue'

export const ALL_NUANCE_TAGS: NuanceTag[] = [
  'Enabling condition', 'Structural constraint', 'Info-seeking', 'Moral licensing',
  'Status / image', 'Temporal discounting', 'Social proof', 'Ambiguous intent',
  'Anxiety buffer', 'Boundary object', 'Tradeoff acknowledged', 'Habit residue'
]

export interface SiteCalibration {
  defaultTemporal: number
  defaultSpatial: number
  defaultSocial: number
  defaultHypothetical: number
  recurringRituals: string[]
}
